/**
 * Minimal UI translation. The English text in the code is the key; `tr("Save")` returns the
 * Swedish text when Swedish is chosen and a translation exists, otherwise the English text.
 * A missing translation can therefore never break a page, it just shows English.
 *
 * The language is kept per browser (localStorage). Changing it reloads the page so every
 * component, including toasts fired from Redux actions, picks it up without extra wiring.
 */
import sv from "./sv";
import en from "./en";

export const LANGUAGES = [
  { code: "sv", label: "Svenska" },
  { code: "en", label: "English" },
];
const STORAGE_KEY = "notiz.lang";
const dictionaries = { sv, en };

function detectLang() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && LANGUAGES.some((l) => l.code === saved)) return saved;
  } catch (e) {
    // storage blocked: fall through to the browser language
  }
  const nav = (typeof navigator !== "undefined" && (navigator.language || "")) || "";
  return nav.toLowerCase().startsWith("sv") ? "sv" : "en";
}

let current = typeof window !== "undefined" ? detectLang() : "en";
if (typeof document !== "undefined") document.documentElement.lang = current;

export function getLang() {
  return current;
}

export function setLang(code) {
  if (!LANGUAGES.some((l) => l.code === code) || code === current) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch (e) {
    // not persisted; still switch for this page load
  }
  window.location.reload();
}

/**
 * Translate an English UI string. `{name}` placeholders are filled from `vars`.
 * @param {string} text
 * @param {Record<string, string|number>} [vars]
 */
export function tr(text, vars) {
  if (typeof text !== "string") return text;
  const dict = dictionaries[current];
  let out = (dict && dict[text]) || text;
  if (vars) out = out.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
  return out;
}
